import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  sendPasswordResetEmail,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword as firebaseUpdatePassword,
  sendEmailVerification
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { checkAndProcessRenewal } from '../services/creditService';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [authError, setAuthError] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [isInitialLogin, setIsInitialLogin] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        // Check if we're on the registration page (either /register or /app/register)
        const isOnRegisterPage = window.location.pathname === '/register' || window.location.pathname === '/app/register';
        const hasReferralCode = new URLSearchParams(window.location.search).get('ref');
        
        // Don't redirect if we're on the register page with a referral code
        if (isOnRegisterPage && hasReferralCode) {
          console.log('On register page with referral code, preventing redirect');
          setLoading(false);
          return;
        }
        
        if (user) {
          // Get user data from Firestore
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          
          // If we can't get the user doc, it might be because they just registered
          // and were signed out automatically
          if (!userDoc.exists()) {
            setUser(null);
            setIsAdmin(false);
            setIsActive(false);
            setAuthError('');
            setLoading(false);
            return;
          }

          const userData = userDoc.data();

          // Check and process credit renewal if needed
          // This ensures monthly credits are refilled appropriately
          await checkAndProcessRenewal(user.uid);

          // Check if user is active and admin
          const isAdmin = userData?.isAdmin ?? false;
          const isActive = userData?.isActive ?? false;

          // Check if the user is email verified or if the account is already activated by admin
          const isVerified = user.emailVerified || isActive;

          if (!isVerified && !isRegistering) {
            // Sign out unverified users only if not in registration process
            await signOut(auth);
            setUser(null);
            setAuthError('Your account is not yet activated. Please verify your email or contact an administrator.');
          } else {
            // If user is email verified but not marked as active in Firestore, update it
            if (user.emailVerified && !isActive) {
              await updateDoc(doc(db, 'users', user.uid), {
                isActive: true,
                updatedAt: new Date().toISOString()
              });
              // Update local state
              const updatedIsActive = true;
              setIsActive(updatedIsActive);
            } else {
              setIsActive(isActive);
            }

            setUser(user);
            setIsAdmin(isAdmin);
            setAuthError('');
            
            // Only navigate on initial login and if we're on the auth page or landing page
            // Don't redirect if we're on the /register page with a referral code
            const isOnRegisterPage = window.location.pathname === '/register';
            if ((user.emailVerified || isActive) && !isRegistering && isInitialLogin && 
                (window.location.pathname === '/auth' || window.location.pathname === '/')) {
              setIsInitialLogin(false);
              navigate('/app');
            }
          }
        } else {
          setUser(null);
          setIsAdmin(false);
          setIsActive(false);
          setAuthError('');
          
          // Reset initial login flag when user signs out
          setIsInitialLogin(true);
          
          // Redirect to auth page if not already there and not on register page or landing page
          const isOnRegisterPage = window.location.pathname === '/register';
          const isOnLandingPage = window.location.pathname === '/';
          if (window.location.pathname !== '/auth' && !isOnRegisterPage && !isOnLandingPage) {
            navigate('/auth');
          }
        }
      } catch (error) {
        // Ignore permission errors during registration flow
        if (error.code === 'permission-denied' && !user) {
          setUser(null);
          setIsAdmin(false);
          setIsActive(false);
          setAuthError('');
        } else {
          console.error('Error in auth state change:', error);
          setUser(null);
          setIsAdmin(false);
          setIsActive(false);
          setAuthError(error.message);
        }
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, [isRegistering, navigate, isInitialLogin]);

  // Update referral statistics when a user becomes active
  useEffect(() => {
    if (user && isActive) {
      const updateReferralStats = async () => {
        try {
          // Get the user's data to check for referral info
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (!userDoc.exists()) return;
          
          const userData = userDoc.data();
          
          if (userData && userData.referrerId) {
            console.log('Updating activation status for referred user:', user.uid);
            
            try {
              // Update the centralized referral system document for this partner
              const referrerId = userData.referrerId;
              console.log('Updating referral system for referrer:', referrerId);
              
              // Get the current referral document
              const referralDocRef = doc(db, 'referralSystem', referrerId);
              const referralDoc = await getDoc(referralDocRef);
              
              if (referralDoc.exists()) {
                console.log('Referral document exists, updating activation status');
                // Document exists, update it
                const referralData = referralDoc.data();
                const referredUsers = referralData.referredUsers || [];
                const referralStats = referralData.referralStats || { total: 0, active: 0 };
                let updated = false;
                
                // Get the user's current subscription data
                const subscription = userData.subscription || null;
                
                // Format subscription data to ensure consistent structure
                const formattedSubscription = subscription ? {
                  active: subscription.status === 'active',
                  planType: subscription.type || null,
                  planName: subscription.planName || null,
                  planPrice: subscription.price || 0,
                  startDate: subscription.startDate || null,
                  cancelDate: subscription.cancelDate || null,
                  status: subscription.status || 'inactive'
                } : {
                  active: false,
                  status: 'inactive'
                };
                
                // Find this user in the referred users list and update activation status and subscription
                const userIndex = referredUsers.findIndex(ru => ru.userId === user.uid);
                
                if (userIndex !== -1) {
                  console.log('Updating user in referred list with subscription:', formattedSubscription);
                  // Update existing user
                  referredUsers[userIndex] = {
                    ...referredUsers[userIndex],
                    subscription: formattedSubscription,
                    activatedAt: referredUsers[userIndex].activatedAt || (formattedSubscription.active ? new Date().toISOString() : null)
                  };
                  updated = true;
                } else {
                  // User not found in referred users list, add them
                  console.log('User not found in referred list, adding with subscription:', formattedSubscription);
                  referredUsers.push({
                    userId: user.uid,
                    email: userData.email || '',
                    fullName: userData.fullName || '',
                    registeredAt: userData.createdAt || new Date().toISOString(),
                    activatedAt: formattedSubscription.active ? new Date().toISOString() : null,
                    subscription: formattedSubscription
                  });
                  updated = true;
                }
                
                if (updated) {
                  // Count active subscriptions
                  const activeCount = referredUsers.filter(ru => ru.subscription && ru.subscription.active).length;
                  referralStats.active = activeCount;
                  
                  // Update the document
                  await updateDoc(referralDocRef, {
                    referredUsers: referredUsers,
                    referralStats: referralStats,
                    updatedAt: new Date().toISOString()
                  });
                  console.log('Updated referral system with activation status and subscription data');
                } else {
                  console.log('No updates needed for referral system');
                }
              } else {
                console.log('Referral document does not exist for referrer:', referrerId);
              }
            } catch (updateError) {
              console.error('Error updating referral system activation status:', updateError);
            }
          }
        } catch (error) {
          console.error('Error updating referral statistics:', error);
        }
      }
      
      updateReferralStats();
    }
  }, [user, isActive]);

  // Update referral system when a user's subscription changes
  const updateReferralSystemWithSubscription = async (userId, subscriptionData) => {
    try {
      // Get the user document to find their referrer
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) return;
      
      const userData = userDoc.data();
      const referrerId = userData.referrerId;
      
      // If user was referred, update the referral system
      if (referrerId) {
        console.log(`Updating referral system for user ${userId} with subscription data`);
        
        const referralDocRef = doc(db, 'referralSystem', referrerId);
        const referralDoc = await getDoc(referralDocRef);
        
        if (referralDoc.exists()) {
          const referralData = referralDoc.data();
          const referredUsers = referralData.referredUsers || [];
          const referralStats = referralData.referralStats || { total: 0, active: 0 };
          
          // Find the user in the referred users list
          const userIndex = referredUsers.findIndex(ru => ru.userId === userId);
          
          if (userIndex !== -1) {
            // Update the user's subscription data
            referredUsers[userIndex].subscription = subscriptionData;
            
            // Update activation status if not already set
            if (!referredUsers[userIndex].activatedAt && subscriptionData && subscriptionData.active) {
              referredUsers[userIndex].activatedAt = new Date().toISOString();
            }
            
            // Count active subscriptions
            const activeCount = referredUsers.filter(ru => ru.subscription && ru.subscription.active).length;
            referralStats.active = activeCount;
            
            // Update the document
            await updateDoc(referralDocRef, {
              referredUsers: referredUsers,
              referralStats: referralStats,
              updatedAt: new Date().toISOString()
            });
            console.log('Updated referral system with subscription data');
          } else {
            // User not found in referred users, add them
            console.log('User not found in referred users list, adding them');
            referredUsers.push({
              userId: userId,
              email: userData.email || '',
              fullName: userData.fullName || '',
              registeredAt: userData.createdAt || new Date().toISOString(),
              activatedAt: subscriptionData && subscriptionData.active ? new Date().toISOString() : null,
              subscription: subscriptionData
            });
            
            // Update stats
            referralStats.total += 1;
            if (subscriptionData && subscriptionData.active) {
              referralStats.active += 1;
            }
            
            // Update the document
            await updateDoc(referralDocRef, {
              referredUsers: referredUsers,
              referralStats: referralStats,
              updatedAt: new Date().toISOString()
            });
            console.log('Added user to referral system with subscription data');
          }
        }
      }
    } catch (error) {
      console.error('Error updating referral system with subscription:', error);
    }
  };

  async function login(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
      const userData = userDoc.data();

      // Check if the user is email verified or if the account is already activated by admin
      const isVerified = userCredential.user.emailVerified || userData?.isActive;

      if (!isVerified) {
        await signOut(auth);
        throw new Error('Your account is not yet activated. Please verify your email or contact an administrator.');
      }
      
      setIsInitialLogin(true);
      return true;
    } catch (error) {
      console.error('Error logging in:', error);
      let message = 'Failed to log in';
      
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        message = 'Invalid email or password';
      } else if (error.code === 'auth/too-many-requests') {
        message = 'Too many failed attempts. Please try again later';
      } else if (error.message) {
        message = error.message;
      }
      
      throw new Error(message);
    }
  }

  async function signup(email, password, fullName, referralCode = '', betaCode = '') {
    if (!email || !password || !fullName) {
      throw new Error('Email, password, and full name are required');
    }

    let isBetaModeEnabled = false;
    
    // First check if beta mode is enabled
    try {
      // Import dynamically to avoid circular dependency
      const { BetaInviteService } = await import('../services/betaInviteService');
      const betaStatus = await BetaInviteService.getPublicBetaStatus();
      
      isBetaModeEnabled = betaStatus.betaModeEnabled;
      
      if (isBetaModeEnabled) {
        // Beta mode is enabled, so we need to validate the beta code
        if (!betaCode || betaCode.trim() === '') {
          throw new Error('Beta code is required for registration during beta phase.');
        }
        
        // We'll validate the beta code after creating the user account
        // This is because we need the user ID to mark the beta code as used
        console.log('Beta code provided for validation:', betaCode);
      }
    } catch (error) {
      if (error.message.includes('Beta code is required')) {
        throw error; // Re-throw our custom error
      }
      // If there's an error checking beta settings, continue with registration
      console.error('Error checking beta settings:', error);
    }
    
    // Only check referral requirements if beta mode is NOT enabled
    if (!isBetaModeEnabled && (!referralCode || referralCode.trim() === '')) {
      try {
        // Import dynamically to avoid circular dependency
        const { SystemSettingsService } = await import('../services/systemSettingsService');
        // Use the public method that doesn't require authentication
        const restrictions = await SystemSettingsService.getPublicSignUpRestrictions();
        
        if (restrictions.disableRegularSignUp) {
          throw new Error('Regular sign-ups are currently disabled. Please use a valid affiliate link to register.');
        }
      } catch (error) {
        if (error.message.includes('Regular sign-ups are currently disabled')) {
          throw error; // Re-throw our custom error
        }
        // If there's an error checking the setting, continue with registration
        console.error('Error checking sign-up settings:', error);
      }
    }

    setIsRegistering(true);
    let userCredential;
    
    try {
      // Create user in Firebase Auth
      userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Validate beta code if beta mode is enabled
      if (betaCode && betaCode.trim() !== '') {
        try {
          const { BetaInviteService } = await import('../services/betaInviteService');
          const betaValidation = await BetaInviteService.validateAndUseBetaCode(betaCode, user.uid, email);
          
          if (!betaValidation.valid) {
            // If beta code is invalid, delete the user and throw an error
            await user.delete();
            throw new Error(betaValidation.message || 'Invalid beta code');
          }
          
          console.log('Beta code validated successfully');
        } catch (betaError) {
          // If there's an error validating the beta code, delete the user and throw an error
          if (user) {
            try {
              await user.delete();
            } catch (deleteError) {
              console.error('Error deleting user after beta code validation failure:', deleteError);
            }
          }
          
          throw new Error(betaError.message || 'Error validating beta code');
        }
      }
      
      // Send email verification
      await sendEmailVerification(user);
      
      // Get referral bonus settings and referrer ID
      let referralBonusCredits = 0;
      let referrerId = null;
      
      if (referralCode) {
        console.log('Looking for referrer with code:', referralCode);
        
        // Try to find the referrer in a way that works with the current Firestore rules
        try {
          // Get all documents from the referralSystem collection
          const referralSystemRef = collection(db, 'referralSystem');
          const referralSnapshot = await getDocs(referralSystemRef);
          
          // Manually find the document with the matching code
          for (const doc of referralSnapshot.docs) {
            const data = doc.data();
            if (data.code === referralCode) {
              referrerId = data.partnerId;
              console.log('Found referrer in referralSystem collection:', referrerId);
              break;
            }
          }
          
          // If not found in referralSystem, try users collection
          if (!referrerId) {
            const usersRef = collection(db, 'users');
            const usersSnapshot = await getDocs(usersRef);
            
            for (const doc of usersSnapshot.docs) {
              const data = doc.data();
              if (data.partnerReferralCode === referralCode) {
                referrerId = doc.id;
                console.log('Found referrer in users collection:', referrerId);
                break;
              }
            }
          }
          
          // If still not found, try direct match with user ID
          if (!referrerId) {
            try {
              const directUserDoc = await getDoc(doc(db, 'users', referralCode));
              if (directUserDoc.exists()) {
                referrerId = referralCode;
                console.log('Found referrer by direct ID match:', referrerId);
              } else {
                console.log('No referrer found for code:', referralCode);
              }
            } catch (directError) {
              console.error('Error checking direct ID match:', directError);
            }
          }
        } catch (error) {
          console.error('Error finding referrer:', error);
          // Continue registration even if referrer lookup fails
        }
        
        // Get referral bonus settings
        try {
          const settingsRef = doc(db, 'system_settings', 'referral_settings');
          const settingsDoc = await getDoc(settingsRef);
          
          if (settingsDoc.exists() && settingsDoc.data().bonusCredits) {
            const bonusCredits = parseInt(settingsDoc.data().bonusCredits);
            referralBonusCredits = isNaN(bonusCredits) ? 500 : bonusCredits;
            console.log('Referral bonus credits:', referralBonusCredits);
          } else {
            // Default value if settings don't exist
            referralBonusCredits = 500;
            console.log('Using default referral bonus: 500');
          }
        } catch (error) {
          console.error('Error processing referral:', error);
          // Use default value if there's an error
          referralBonusCredits = 500;
        }
      }
      
      // Create user document in Firestore
      const userData = {
        email: email,
        fullName: fullName,
        isAdmin: false,
        isActive: false, // Will be set to true after email verification
        referralCode: referralCode, // Store the referral code if provided
        referrerId: referrerId, // Store the referrer ID if found
        referralProcessed: true, // Mark as processed immediately
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        credits: {
          total: 0, // Default starting credits
          used: 0,
          lastRefillDate: new Date().toISOString(),
          flex: referralBonusCredits > 0 ? referralBonusCredits : 0 // Add bonus flex credits if referred
        }
      };
      
      console.log('Creating user with credits:', userData.credits);
      await setDoc(doc(db, 'users', user.uid), userData);
      
      // If this user was referred, update the referral system immediately
      if (referrerId) {
        try {
          console.log('Updating referral system for referrer:', referrerId);
          const referralDocRef = doc(db, 'referralSystem', referrerId);
          const referralDoc = await getDoc(referralDocRef);
          
          if (referralDoc.exists()) {
            console.log('Referral document exists, updating it');
            // Document exists, update it
            const referralData = referralDoc.data();
            const referredUsers = referralData.referredUsers || [];
            const referralStats = referralData.referralStats || { total: 0, active: 0 };
            
            // Add this user if not already in the list
            if (!referredUsers.some(ru => ru.userId === user.uid)) {
              console.log('Adding user to referred users list');
              referredUsers.push({
                userId: user.uid,
                email: email,
                fullName: fullName,
                registeredAt: new Date().toISOString(),
                activatedAt: null, // Will be updated when user activates their account
                subscription: null // Will be updated when user subscribes
              });
              
              referralStats.total += 1;
              
              // Update the document
              await updateDoc(referralDocRef, {
                referredUsers: referredUsers,
                referralStats: referralStats,
                updatedAt: new Date().toISOString()
              });
              console.log('Updated referral system document with new referred user');
            }
          } else {
            console.log('Referral document does not exist, creating it');
            // Get partner information from users collection
            const partnerDoc = await getDoc(doc(db, 'users', referrerId));
            let partnerName = '';
            let partnerEmail = '';
            let partnerCode = referralCode || '';
            
            if (partnerDoc.exists()) {
              const partnerData = partnerDoc.data();
              partnerName = partnerData.fullName || '';
              partnerEmail = partnerData.email || '';
              partnerCode = partnerData.partnerReferralCode || partnerCode;
              console.log('Found partner data:', partnerName, partnerEmail, partnerCode);
            }
            
            // Document doesn't exist, create it
            await setDoc(referralDocRef, {
              partnerId: referrerId,
              partnerName: partnerName,
              partnerEmail: partnerEmail,
              code: partnerCode,
              commission: 10, // Default commission rate
              isActive: true,
              referredUsers: [{
                userId: user.uid,
                email: email,
                fullName: fullName,
                registeredAt: new Date().toISOString(),
                activatedAt: null, // Will be updated when user activates their account
                subscription: null // Will be updated when user subscribes
              }],
              referralStats: { total: 1, active: 0 },
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            });
            console.log('Created new referral system document with referred user');
            
            // Also update the user document to mark them as a referral partner
            await updateDoc(doc(db, 'users', referrerId), {
              isReferralPartner: true
            }).catch(err => {
              console.log('Could not update referrer document, but referral system document was created');
            });
          }
        } catch (referralError) {
          console.error('Error updating referral system during registration:', referralError);
          // Continue with registration even if referral update fails
        }
      }
      
      // Sign out immediately since the account needs verification
      await signOut(auth);
      setIsRegistering(false);
      return true;
    } catch (error) {
      console.error('Error registering:', error);
      // If we created the auth user but failed to create the document, clean up
      if (userCredential?.user) {
        try {
          await userCredential.user.delete();
        } catch (deleteError) {
          console.error('Error deleting auth user after Firestore failure:', deleteError);
        }
      }

      let message = 'Failed to register';
      if (error.code === 'auth/email-already-in-use') {
        message = 'Email is already registered';
      } else if (error.code === 'auth/invalid-email') {
        message = 'Invalid email address';
      } else if (error.code === 'auth/weak-password') {
        message = 'Password is too weak';
      }
      
      setIsRegistering(false);
      throw new Error(message);
    }
  }

  async function logout() {
    try {
      await signOut(auth);
      // Navigation will be handled by the auth state change listener
    } catch (error) {
      console.error('Error logging out:', error);
      throw error;
    }
  }

  async function resetPassword(email) {
    if (!email) {
      throw new Error('Email is required');
    }
    
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Error sending password reset email:', error);
      let message = 'Failed to send password reset email';
      
      if (error.code === 'auth/user-not-found') {
        message = 'No account found with this email';
      } else if (error.code === 'auth/invalid-email') {
        message = 'Invalid email address';
      } else if (error.message) {
        message = error.message;
      }
      
      throw new Error(message);
    }
  }

  async function updatePassword(currentPassword, newPassword) {
    if (!user) {
      throw new Error('Must be logged in to change password');
    }

    if (!currentPassword || !newPassword) {
      throw new Error('Both current and new password are required');
    }

    try {
      // First reauthenticate the user
      const credential = EmailAuthProvider.credential(
        user.email,
        currentPassword
      );
      await reauthenticateWithCredential(user, credential);

      // Then update the password
      await firebaseUpdatePassword(user, newPassword);
    } catch (error) {
      console.error('Error updating password:', error);
      let message = 'Failed to update password';
      
      if (error.code === 'auth/wrong-password') {
        message = 'Current password is incorrect';
      } else if (error.code === 'auth/weak-password') {
        message = 'New password is too weak';
      } else if (error.message) {
        message = error.message;
      }
      
      throw new Error(message);
    }
  }

  async function updateFullName(newFullName) {
    if (!user) {
      throw new Error('Must be logged in to update profile');
    }

    if (!newFullName || !newFullName.trim()) {
      throw new Error('Full name is required');
    }

    try {
      // Update the user document in Firestore
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        fullName: newFullName.trim(),
        updatedAt: new Date().toISOString()
      });
      
      return true;
    } catch (error) {
      console.error('Error updating full name:', error);
      throw new Error('Failed to update full name. Please try again.');
    }
  }

  async function getReferralData(userId) {
    if (!userId) return null;
    
    try {
      // First check if the user is a referral partner
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) return null;
      
      const userData = userDoc.data();
      if (!userData.isReferralPartner) return null;
      
      // Get the referral system document for this partner
      const referralDoc = await getDoc(doc(db, 'referralSystem', userId));
      
      if (referralDoc.exists()) {
        return referralDoc.data();
      } else {
        // Create an empty referral document if it doesn't exist
        const emptyReferralData = {
          partnerId: userId,
          isActive: true,
          referredUsers: [],
          referralStats: { total: 0, active: 0 },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        // Save the empty document
        await setDoc(doc(db, 'referralSystem', userId), emptyReferralData);
        return emptyReferralData;
      }
    } catch (error) {
      console.error('Error getting referral data:', error);
      return null;
    }
  }

  const value = {
    user,
    isAdmin,
    isActive,
    loading,
    authError,
    isRegistering,
    setIsRegistering,
    login,
    logout,
    signup,
    resetPassword,
    updatePassword,
    updateFullName,
    getReferralData,
    updateReferralSystemWithSubscription
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
